import React, { ReactNode } from "react";
import { Collapse, CollapseProps, Stack, StackProps } from "@mui/material";
import { merge } from "lodash";
import { ConditionalWrap } from "shared-ts-ui-react";

export interface Step {
  key?: React.Key;
  contentWrapperProps?: StackProps;
  disableContentWrapper?: boolean;
  content: ReactNode;
  /** Sequential condition (used only when showAllSteps === false). Defaults to true when omitted. */
  when?: boolean;
  /** Hard visibility guard. If false, the step is never shown (even when showAllSteps === true). Defaults to true. */
  visibleWhen?: boolean;
}

export interface FormStepsProps {
  steps: Step[];
  stackProps?: StackProps;
  collapseProps?: Partial<CollapseProps>;
  /**
   * When true, disables sequential gating and shows all steps that pass `visibleWhen`.
   * (Ignores each step’s `when`, but still honors `visibleWhen`.)
   * Default is false.
   */
  showAllSteps?: boolean;
}

/**
 * FormSteps reveals an ordered list of UI fragments one-by-one as their conditions become true.
 * Each item is wrapped in a MUI Collapse for smooth enter/exit animations.
 *
 * Usage:
 * <FormSteps
 *   steps={[
 *     { content: <First />, when: true },
 *     { content: <Second />, when: condition2 },
 *     { content: <Third />, when: condition3 },
 *   ]}
 * />
 *
 * To disable step-by-step gating (e.g., when editing existing entities), set `showAllSteps` to true.
 */
export const FormSteps: React.FC<FormStepsProps> = ({
  steps,
  stackProps,
  collapseProps,
  showAllSteps = false,
}) => {
  // Ensure sequential gating: a step is shown only if all previous steps' conditions are satisfied.
  let allPrevSatisfied = true;
  return (
    <Stack {...stackProps}>
      {steps.map((step, idx) => {
        // If a `when` property is provided, coerce it to boolean. Only default to true when `when` is not provided at all.
        const hasWhen = Object.prototype.hasOwnProperty.call(step, "when");
        const condition = hasWhen ? !!step.when : true;
        // Hard visibility guard: if provided, coerce to boolean; default to true when omitted.
        const hasVisibleWhen = Object.prototype.hasOwnProperty.call(step, "visibleWhen");
        const visibleWhen = hasVisibleWhen ? !!step.visibleWhen : true;
        // If showAllSteps is true, ignore both sequential gating and each step's own `when` condition
        const gatingOk = showAllSteps ? true : allPrevSatisfied;
        const show = visibleWhen && (showAllSteps ? true : gatingOk && condition);
        const key = step.key ?? idx;
        // Update cumulative flag for subsequent steps only when gating is active and the step is visible in principle
        if (!showAllSteps && visibleWhen) {
          allPrevSatisfied = allPrevSatisfied && condition;
        }

        console.log(
          `FormStep[${key}]: show=${show}, visibleWhen=${visibleWhen}, condition=${condition}, gatingOk=${gatingOk}, allPrevSatisfied=${allPrevSatisfied}, showAllSteps=${showAllSteps}`
        );

        return (
          <Collapse
            key={key}
            in={show}
            timeout={collapseProps?.timeout ?? "auto"}
            mountOnEnter={collapseProps?.mountOnEnter ?? true}
            unmountOnExit={collapseProps?.unmountOnExit ?? false}
            {...collapseProps}
          >
            <ConditionalWrap
              condition={step.disableContentWrapper !== true}
              wrap={(children) => (
                <Stack
                  useFlexGap
                  sx={merge({ gap: 2 }, step.contentWrapperProps?.sx)}
                >
                  {children}
                </Stack>
              )}
            >
              {step.content}
            </ConditionalWrap>
          </Collapse>
        );
      })}
    </Stack>
  );
};
